import MicroModal from 'micromodal';

const externalLinks = () => {
    let pendingExternalUrl = null;

    /**
     * Check if a link is external
     * @param {HTMLAnchorElement} link - The anchor element to check
     * @returns {boolean} - True if link is external
     */
    function isExternalLink(link) {
        // Skip if not an anchor tag or no href
        if (!link.href) return false;

        // Skip special protocols
        if (
            link.href.startsWith('mailto:') ||
            link.href.startsWith('tel:') ||
            link.href.startsWith('javascript:') ||
            link.href.startsWith('#')
        ) {
            return false;
        }

        // Get current hostname and link hostname
        const currentDomain = window.location.hostname;

        try {
            const linkUrl = new URL(link.href);
            const linkDomain = linkUrl.hostname;

            // Check if domains are different
            return linkDomain !== currentDomain && linkDomain !== '';
        } catch (e) {
            // If URL parsing fails, assume it's internal
            return false;
        }
    }

    /**
     * Handle external link clicks
     * @param {Event} event - Click event
     */
    function handleExternalLinkClick(event) {
        const link = event.currentTarget;

        if (isExternalLink(link)) {
            // Prevent default navigation
            event.preventDefault();
            event.stopPropagation();

            // Store the target URL
            pendingExternalUrl = link.href;

            // Check if link should open in new tab
            const openInNewTab = link.target === '_blank';

            // Store the target attribute for later use
            if (openInNewTab) {
                pendingExternalUrl = { url: link.href, newTab: true };
            } else {
                pendingExternalUrl = { url: link.href, newTab: false };
            }

            // Open the modal
            MicroModal.show('modal-external-link', {
                onShow: (modal) => {
                    const buttons = modal.querySelectorAll('button');
                    buttons.forEach(button => {
                        button.setAttribute('tabindex', '0');
                        button.removeAttribute('aria-hidden');
                    });
                },
                onClose: (modal) => {
                    const buttons = modal.querySelectorAll('button');
                    buttons.forEach(button => {
                        button.setAttribute('tabindex', '-1');
                        button.setAttribute('aria-hidden', 'true');
                    });
                    // Reset pending URL when modal is closed
                    pendingExternalUrl = null;
                },
                awaitCloseAnimation: true,
                disableScroll: true
            });
        }
    }

    /**
     * Handle confirm button click in modal
     */
    function handleConfirmClick() {
        if (pendingExternalUrl) {
            const { url, newTab } = pendingExternalUrl;

            if (newTab || true) { // Always open in new tab for security
                // Open in new tab with security attributes
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                // Navigate in same window
                window.location.href = url;
            }

            // Close the modal
            MicroModal.close('modal-external-link');

            // Reset pending URL
            pendingExternalUrl = null;
        }
    }

    /**
     * Initialize external link detection
     */
    function init() {
        // Get all links on the page
        const allLinks = document.querySelectorAll('a[href]');

        // Add click listeners to all links
        allLinks.forEach(link => {
            link.addEventListener('click', handleExternalLinkClick);
        });

        // Add listener to confirm button
        const confirmButton = document.getElementById('modal-external-link-confirm');
        if (confirmButton) {
            confirmButton.addEventListener('click', handleConfirmClick);
        }

        // Watch for dynamically added links (for Vue apps, carousels, etc.)
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    // Check if added node is a link
                    if (node.nodeType === 1) { // Element node
                        if (node.tagName === 'A' && node.href) {
                            node.addEventListener('click', handleExternalLinkClick);
                        }

                        // Check for links within added node
                        const links = node.querySelectorAll && node.querySelectorAll('a[href]');
                        if (links) {
                            links.forEach(link => {
                                link.addEventListener('click', handleExternalLinkClick);
                            });
                        }
                    }
                });
            });
        });

        // Start observing the document for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
};

export default externalLinks;
